import {EventVerteiler} from "../global/eventVerteiler";
import {curry} from "../global/functionalTools";
import type {BoxRelativeToPart, OwayoKonfiPart} from "./owayoPartWithSelObs";
import {PortalLine} from "./portallinie";
import {ZOrderCollisionFinder} from "./zOrderCollisionFinder";
import {owayoUtils} from "./owayoUtils";
import {Handles} from "./handles";
import {THREE} from "./resource/BVH.js";
import {CONSTANTS} from "./meta_data";
import {HINTBOXEVENTNAMES} from "../global/GlobalEventnames";
import {Vector2, Vector2WithUV, WidthHeight} from "./delme_globals";
import {deepCopy} from "../global/deepCopy";
import {SelObTypes} from "./SelObTypes";
import type {PartSelObShaderInfoProvider} from "@k/js/shader/PartSelObShaderInfoProvider";
export interface PartSelObInteractionStartObjectParameters {
    lastScalingOfMouseOverPart?:Vector2;
    scaling? : Vector2;
    translationOffset? : Vector2;
    rotation? : number;
    rotationOffset? : number;
    renderCanvasPosition? : Vector2;
    selObCenterOnScreen? : Vector2;
    position? : Vector2WithUV;
    lastRenderCanvasPositionOfMouseOverPart? : Vector2;
}

export class PartSelObInteractionStartObject {
    scaling             : Vector2;
    translationOffset   : Vector2;
    rotation            : number;
    rotationOffset      : number;
    renderCanvasPosition: Vector2;
    selObCenterOnScreen : Vector2;
    position            : Vector2WithUV;
    lastRenderCanvasPositionOfMouseOverPart : Vector2;
    lastScalingOfMouseOverPart : Vector2;

    constructor(params : PartSelObInteractionStartObjectParameters) {
        this.scaling = params.scaling || new Vector2();
        this.translationOffset = params.translationOffset || new Vector2();
        this.rotation = params.rotation || 0;
        this.rotationOffset = params.rotationOffset || 0;
        this.renderCanvasPosition = params.renderCanvasPosition || new Vector2();
        this.selObCenterOnScreen = params.selObCenterOnScreen || new Vector2();
        this.position = params.position ||  new Vector2WithUV();
        this.lastRenderCanvasPositionOfMouseOverPart = params.lastRenderCanvasPositionOfMouseOverPart || new Vector2();
        this.lastScalingOfMouseOverPart  = params.lastScalingOfMouseOverPart || new Vector2();
    }
}

class PartSelObConflictStates {
    isInSperrbezirk     =false;
    illegalCollision    =false;
}

class PartSelObParentDimensionInfo {
    public textureSize;
    public partStauchungsFaktor;
    public partSizeCM;
    public sperrBezirke;
    public usedScaleBecauseOfMaxTextureSize;
    public druckbereich:BoxRelativeToPart|null = null;
    public nichtDruckBereich:BoxRelativeToPart|null = null
}




export abstract class PartSelectableObject implements PartSelObShaderInfoProvider {
    get parentDimensionInfos(): PartSelObParentDimensionInfo {
        return this._parentDimensionInfos as PartSelObParentDimensionInfo;
    }
    id;
    type;
    parent:OwayoKonfiPart;
    rotation;
    relSize! : Vector2;
    DBRotatedRelSize!: Vector2;
    translation!: Vector2;
    repeat =false;
    clampToColor =-1;
    wasserzeichen =false;
    wasserzeichenInvertImage = false;
    wasserzeichenIntensity = -1;
    shader : THREE.ShaderMaterial|null;
    indexInShader =-1;
    interactionStart : PartSelObInteractionStartObject;
    clonedByPortalLineNumber : number[] =[];
    cloneIds : string[] = [];
    corners : Vector2[] = [];
    cornersInLocalCanvas : Vector2[] = [];
    matrixAbsUVFromAbsCanvas : THREE.Matrix3;
    protected matrixAbsCanvasFromAbsUV : THREE.Matrix3;
    isPinned =false
    floating = 0;
    keepSeparate =false;
    allowDelete  = true;
    allowDrag  = true;
    allowResize = true;
    allowColorChange = true;
    isVorbelegung = false;
    conflicts : PartSelObConflictStates;
    insertTargetSize  = 0;
    handles;
    isPattern:boolean =false;
    isAILogo:boolean=false;
    hasTackleTwill:boolean=false;
    berechnungsFeature;
    public textureCanvas:HTMLCanvasElement;
    public textureContext2D:CanvasRenderingContext2D;
    public texture:THREE.Texture;
    public textureSize:Vector2;
    private _parentDimensionInfos : PartSelObParentDimensionInfo|null = null;
    public margin:Vector2;
    public scaling!:Vector2;

    public static SHOWONALLCOLORS = -1;
    abstract init(callback?:()=>void|null);

    protected constructor(params) {
        this.id = params.id;
        this.type = params.type || "bogusType";

        this.parent = params.parent;
        this.initTransformations(params);
        this.generateTexture();
        this.repeat = params.repeat || false;

        /** ColorcodeNr auf dem das Logo Exclusiv angezeigt werden soll */
        this.clampToColor = params.clampToColor || PartSelectableObject.SHOWONALLCOLORS;
        // this.initWasserzeichenProperties(params);
        this.shader = null;
        this.indexInShader = 0;

        /**
         * Da texturen von den Dimensionen her Power of 2 sein müssen, ergibt sich ein Rand
         * zwischen Texturrand und tatsächtlichem Inhalt. Der Inhalt wird zentriert, d.h. oben und unten
         * (sowie links und rechts) ist der selbe Rand
         */
        this.margin = new Vector2();
        this.interactionStart = new PartSelObInteractionStartObject({
            scaling : new Vector2(this.scaling.x, this.scaling.y),
            translationOffset : new Vector2(0,0),
            rotation: this.rotation
        });

        this.clonedByPortalLineNumber = [];
        this.cloneIds = [];

        this.corners = PartSelectableObject.getInitialCorners();
        this.cornersInLocalCanvas =  PartSelectableObject.getInitialCorners();
        this.matrixAbsUVFromAbsCanvas = new THREE.Matrix3();
        this.matrixAbsCanvasFromAbsUV = new THREE.Matrix3();

        this.isPinned = params.isPinned || false;
        this.hasTackleTwill = params.hasTackleTwill || false;
        this.floating        = params.floating    || CONSTANTS.FLOAT_FREE;
        this.keepSeparate       = params.keepSeparate   || false;
        this.initAllowedActions(params);
        this.conflicts      = new PartSelObConflictStates()

        this.isVorbelegung = params.isVorbelegung || false;
        this.insertTargetSize = params.insertTargetSize || 10.0 ;
        this.setParentDimensionInfos(params.parent);
    }


    getMatrixAbsCanvasFromAbsUV() {
        return this.matrixAbsCanvasFromAbsUV;
    }

    private static getInitialCorners = ()=>[1,2,3,4].map(()=>new Vector2(0,0));

    public generateTexture() {
        this.textureCanvas = document.createElement('canvas');
        this.textureContext2D = this.textureCanvas.getContext('2d') as CanvasRenderingContext2D;
        this.texture = PartSelectableObject.createTHREETexture(this.textureCanvas);
        this.textureSize =  new Vector2(0,0);
    }

    public static createTHREETexture(textureCanvas:HTMLCanvasElement) {
        const texture = new THREE.Texture(textureCanvas);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        return texture;
    }

    public getTexture = ()=>this.texture;

    protected setParentDimensionInfos(parent:OwayoKonfiPart) {
        this._parentDimensionInfos = new PartSelObParentDimensionInfo();
        this._parentDimensionInfos.textureSize = parent.textureSize;
        this._parentDimensionInfos.partStauchungsFaktor = parent.partStauchungsFaktor;
        this._parentDimensionInfos.partSizeCM = parent.partSizeCM;
        this._parentDimensionInfos.sperrBezirke = parent.sperrBezirke;
        this._parentDimensionInfos.usedScaleBecauseOfMaxTextureSize = parent.usedScaleBecauseOfMaxTextureSize;
        this._parentDimensionInfos.druckbereich = parent.druckBereich;
        this.parentDimensionInfos.nichtDruckBereich = parent.nichtDruckBereich;
    }

    public getParent() {
        return this.parent;
    }

    public getParentPartName() {
        return this.getParent().name;
    }
    public setParent(parent) {
        this.parent = parent;
    }

    protected getPartSelOb(id) {
        return this.getParent().parent.getPartSelObById(id);
    }

    private initTransformations(params) {
        this.rotation = params.rotation || 0;
        this.scaling = params.scaling || new Vector2();
        this.relSize = params.relSize || new Vector2();
        this.DBRotatedRelSize = params.DBRotatedRelSize || new Vector2();
        this.translation = params.translation || new Vector2();
    }

    private initAllowedActions(params) {
        this.allowDelete = params.allowDelete !== false;
        this.allowDrag = params.allowDrag !== false;
        this.allowResize = params.allowResize !== false;
        this.allowColorChange = params.allowColorChange || true;
    }

    public createMatrices() {
        this.matrixAbsCanvasFromAbsUV = this.createMatrixAbsCanvasFromAbsUV();
        this.matrixAbsUVFromAbsCanvas = this.createMatrixAbsUVFromAbsCanvas();
    }

    public updateCalculatedValues() {
        this.createMatrices();
        this.corners = this.getCornerCoordinates();
    }

    public getTextureSizeWithoutMargin(dim):number {
        return this.textureSize[dim] - 2 * this.margin[dim];
    }

    public enterConflictedState(state) {
        this.conflicts[state] = true;
        this.withClonesDo(c=>c.conflicts[state] = true);
    }

    protected withClonesDo(callback) {
        if (this.cloneIds.length > 0) {
            for (let i = 0, lenI = this.cloneIds.length; i < lenI; i++) {
                callback(this.getPartSelOb(this.cloneIds[i]));
            }
        }
    }

    public leaveConflictedState(state) {
        this.conflicts[state] = false;
        this.withClonesDo(c=>c.conflicts[state] = false)
    }

    public isConflicted() {
        let conflict, output = false;
        for ( conflict in this.conflicts ) {
            output = output || this.conflicts[conflict];
        }
        return output;
    }

    private isSelecedObjectOrCloneOfSelectedObject() {
        const selectedObject = this.getParent().parent.selectedObject;
        if(selectedObject === null) {
            return false;
        }
        return this  === selectedObject || selectedObject.cloneIds.indexOf(this.id) > -1;
    }

    placeAppropriateHandles() {
        if (!this.isSelecedObjectOrCloneOfSelectedObject()) {
            return;
        }

        const extremes = this.getPartCanvasMinMaxCoordinates();
        const extremesWithTranslation = deepCopy(true, {translationX:this.translation.x, translationY:this.translation.y}, extremes);
        const zIndizesAndWarnings     = this.overlappingObjectZIndizes();
        const zIndizes                = zIndizesAndWarnings.zIndizes;
        const ownIndex                = this.zIndex();
        const allowedOperations       = this.allowedOperations();

        const handlesToShow = {
            pinHandle          : allowedOperations.pin,
            rotateHandle       : allowedOperations.rotate,
            scalingHandle      : allowedOperations.resize,
            deleteHandle       : allowedOperations.deleteIt,
            zOrderUpHandle     : (zIndizes[zIndizes.length-1] > ownIndex) && allowedOperations.zOperations,
            zOrderDownHandle   : (zIndizes[0] < ownIndex) && allowedOperations.zOperations,
            doubleHandle       : allowedOperations.duplicate
        };

        if(!this.editFunctionalityBlacklisted()) {
            this.handleIllegalCollisions(zIndizesAndWarnings.illegalCollisions);
        }
        EventVerteiler.broadcast(
            "zOrderAvailableEvent",
            {
                up  : handlesToShow.zOrderUpHandle,
                down: handlesToShow.zOrderDownHandle
            }
        );
        EventVerteiler.broadcast("paintedOnHandleAndGuideLayerEvent", this.getParentPartName());
        this.handles.place(handlesToShow, extremesWithTranslation);
    }

    public setViewerTask() {
        this.getParent().parent.viewerTasks.addTasks(1);
    }
    public viewerTaskFinished() {
        this.getParent().parent.viewerTasks.taskFinished();
    }

    private handleIllegalCollisions(illegalCollisions) {
        if(illegalCollisions.length > 0) {
            EventVerteiler.broadcast("selObEnteringIllegalCollisionEvent", this.id);
            this.enterConflictedState("illegalCollision");
        } else {
            if (this.conflicts.illegalCollision === true) {
                EventVerteiler.broadcast(HINTBOXEVENTNAMES.SELOBLEAVINGCOLLISION, this.id);
            }
            this.leaveConflictedState("illegalCollision");
        }
    }


    private editFunctionalityBlacklisted() {
        if(SelObTypes.isLogo(this.type)) {
            return owayoUtils.funktionalitaetIsBlacklisted("Logos");
        }
        if(this.type === SelObTypes.TEXT) {
            return owayoUtils.funktionalitaetIsBlacklisted("Texte");
        }
        return false;
    }

    allowedOperations():{resize,deleteIt,move,rotate,zOperations,colorChange,duplicate,pin} {
        const editAllowed = !this.editFunctionalityBlacklisted();
        return {
            resize     	: this.allowResize      && !this.isPinned,
            deleteIt   	: this.allowDelete      && !this.isPinned,
            move       	: this.allowDrag        && !this.isPinned,
            rotate     	: this.allowResize      && !this.isPinned,    //sic! Momentan gibt es kein extra Flag für Rotation
            zOperations	: this.floating === CONSTANTS.FLOAT_FREE   && !this.isPinned && editAllowed,
            colorChange	: this.allowColorChange && !this.isPinned,
            duplicate  	: editAllowed,
            pin			: editAllowed
        };
    }

    getRelativeTranslation(inRelationTo:Vector2) {
        return {
            x: this.translation.x / inRelationTo.x,
            y: this.translation.y / inRelationTo.y
        };
    }

    getRelSize(scalingToUse?) : Vector2{
        if ( typeof(scalingToUse) === "undefined") {
            scalingToUse = this.scaling;
        }
        return new Vector2(
            (this.getTextureSizeWithoutMargin("x") as number) * scalingToUse.x  * this.parentDimensionInfos.partStauchungsFaktor / this.parentDimensionInfos.textureSize.x ,
            (this.getTextureSizeWithoutMargin("y") as number) * scalingToUse.y / this.parentDimensionInfos.textureSize.y
        );
    }

    getSizeInCm(scalingToUse?):Vector2 {
        const size = this.getRelSize(scalingToUse);
        return new Vector2(size.x * this.parentDimensionInfos.partSizeCM.x,size.y * this.parentDimensionInfos.partSizeCM.y);
    }

    getScalingForTargetSize(insertTargetSize?):Vector2 {
        let scale,
            scaleX, scaleXHalfPart;


        if(typeof(insertTargetSize) === "undefined") {
            insertTargetSize = this.insertTargetSize;
        }

        const pxProCmXAchse = this.parentDimensionInfos.textureSize.x / this.parentDimensionInfos.partSizeCM.x;
        const cmSelObWithoutScalingXAchse = this.getTextureSizeWithoutMargin("x") / pxProCmXAchse;

        scaleX = insertTargetSize /  cmSelObWithoutScalingXAchse;
        scaleXHalfPart = ( this.parentDimensionInfos.partSizeCM.x / 2) / cmSelObWithoutScalingXAchse;

        // In der X Richtung wird später wieder der Partstauchungsfactor drauf gerechnet, deswegen müsste das scaling in diese richtung
        // noch etewas kleiner ausfallen. Wir sind zwar jetzt genau auf 10cm. später wird der Partstauchungsfaktor dies auf 10.1 bzw. 9.8 etc. verändern.
        // Deswegen rechnen wir auf dieses skaling den Partstauchungsfaktor
        scaleX = scaleX / this.parentDimensionInfos.partStauchungsFaktor;
        scaleXHalfPart = scaleXHalfPart / this.parentDimensionInfos.partStauchungsFaktor;

        const pxProCmYAchse = this.parentDimensionInfos.textureSize.y / this.parentDimensionInfos.partSizeCM.y;
        const cmSelObWithoutScalingYAchse = this.getTextureSizeWithoutMargin("y") / pxProCmYAchse;

        const scaleY = insertTargetSize   /  cmSelObWithoutScalingYAchse;
        const scaleYHalfPart = ( this.parentDimensionInfos.partSizeCM.y / 2) / cmSelObWithoutScalingYAchse;

        scale = Math.min(scaleY, scaleYHalfPart, scaleX, scaleXHalfPart);
        scale = Math.min( scale, 2.0);
        return new Vector2(scale ,scale);
    }

    setScalingFromRelSize(relSize) {
        const textureSize = this.parentDimensionInfos.textureSize;
        const textureSizeWithoutMarginX = this.getTextureSizeWithoutMargin("x");
        const textureSizeWithoutMarginY = this.getTextureSizeWithoutMargin("y");
        this.scaling.x = relSize.x / this.parentDimensionInfos.partStauchungsFaktor / textureSizeWithoutMarginX * textureSize.x;
        this.scaling.y = relSize.y * textureSize.y / textureSizeWithoutMarginY;
    }

    setScalingToKeepRelHeight(relHeight) {
        const oldScalingY = this.scaling.y;
        this.scaling.y = relHeight * this.parentDimensionInfos.textureSize.y / this.getTextureSizeWithoutMargin("y");
        this.scaling.x = this.scaling.x * this.scaling.y / oldScalingY;
    }

    getRelUVFromAbsUV = (coord:Vector2)=>new Vector2(this.getRelUVFromAbsUV_x(coord.x), this.getRelUVFromAbsUV_y(coord.y))
    getRelUVFromAbsUV_x = x=>x / this.parentDimensionInfos.textureSize.x;
    getRelUVFromAbsUV_y = y=>y / this.parentDimensionInfos.textureSize.y;
    getAbsUVFromRelUV = (x,y)=>new Vector2(this.getAbsUVFromRelUV_x(x), this.getAbsUVFromRelUV_y(y));
    getAbsUVFromRelUV_x = x=>x * this.parentDimensionInfos.textureSize.x;
    getAbsUVFromRelUV_y = y=>y * this.parentDimensionInfos.textureSize.y;

    isOutOfBounds() {
        return (
        this.corners[0].x > this.parentDimensionInfos.textureSize.x //links oben //was heißt seit neuem Koordinaten System "oben"?
        || this.corners[2].x < 0 //rechts unten
        || this.corners[0].y < 0 //links oben
        || this.corners[2].y > this.parentDimensionInfos.textureSize.y); //rechts unten
    }

    getMinX = ()=>this.corners[0].x;
    getMaxX = ()=>this.corners[1].x;
    getMinY = ()=>this.corners[0].y;
    getMaxY = ()=>this.corners[2].y;

    isOutOfDruckBereich() {
        const druckbereich = this.parentDimensionInfos.druckbereich;
        const nichtDruckbereich = this.parentDimensionInfos.nichtDruckBereich;
        const textureSize = this.parentDimensionInfos.textureSize;
        if(druckbereich === null) {
            return false;
        }
        const outOfDruckBereich =  this.getMinX() > druckbereich.topRight.x * textureSize.x ||
            this.getMaxX() < druckbereich.bottomLeft.x * textureSize.x ||
            this.getMaxY() < (1 - druckbereich.bottomLeft.y) * textureSize.y ||
            this.getMinY() > (1 - druckbereich.topRight.y) * textureSize.y;

        if(nichtDruckbereich === null) {
            return outOfDruckBereich;
    }
        return this.getMaxX()< nichtDruckbereich.topRight.x * textureSize.x && this.getMaxY() < (1-nichtDruckbereich.topRight.y) * textureSize.y &&
            this.getMinX() > nichtDruckbereich.bottomLeft.x * textureSize.x && this.getMinY() > (1-nichtDruckbereich.bottomLeft.y) * textureSize.y;

    }



    cloneOnPart(partName) {
        let i, lenI, clone;
        for (i = 0, lenI = this.cloneIds.length; i < lenI; i++) {
            clone = this.getPartSelOb(this.cloneIds[i]);
            if (clone.getParentPartName() === partName) {
                return clone;
            }
        }
        return null;
    }

    getCornerCoordinates() {
        return  [
            this.getCoordinatesAbsUVFromAbsCanvas(this.cornersInLocalCanvas[0].x,this.cornersInLocalCanvas[0].y),
            this.getCoordinatesAbsUVFromAbsCanvas(this.cornersInLocalCanvas[1].x,this.cornersInLocalCanvas[1].y),
            this.getCoordinatesAbsUVFromAbsCanvas(this.cornersInLocalCanvas[2].x,this.cornersInLocalCanvas[2].y),
            this.getCoordinatesAbsUVFromAbsCanvas(this.cornersInLocalCanvas[3].x,this.cornersInLocalCanvas[3].y)
        ];
    }

    isDistinctFromArea(areaCorners) {
        const ownExtremes = this.getPartCanvasMinMaxCoordinates();

        return !(areaCorners.minX < ownExtremes.maxX    && areaCorners.maxX > ownExtremes.minX &&
        areaCorners.minY < ownExtremes.maxY    && areaCorners.maxY > ownExtremes.minY);
    }

    containsArea(areaCorners) {
        const ownExtremes = this.getPartCanvasMinMaxCoordinates();
        return  areaCorners.minX >= ownExtremes.minX && areaCorners.maxX <= ownExtremes.maxX &&
            areaCorners.minY >= ownExtremes.minY && areaCorners.maxY <= ownExtremes.maxY;
    }

    isContainedInArea(areaCorners) {
        const ownExtremes = this.getPartCanvasMinMaxCoordinates();
        return areaCorners.minX <= ownExtremes.minX && areaCorners.maxX >= ownExtremes.maxX &&
            areaCorners.minY <= ownExtremes.minY && areaCorners.maxY >= ownExtremes.maxY;
    }

    /**
     * Gibt ein Array zurück mit den Indizes aller überlappender Objekte
     * @return {{zIndizes, illegalCollisions}}
     */
    overlappingObjectZIndizes() {
        const zIndexFinder = new ZOrderCollisionFinder(this);
        return zIndexFinder.getZIndexCollisions();
    }

    boundingBoxDimensions() {
        const extremes = this.getPartCanvasMinMaxCoordinates();
        return {width:extremes.maxX - extremes.minX, height:extremes.maxY - extremes.minY};
    }

    covers(partName:string, pos:Vector2) {
        if(this.getParentPartName() !== partName) {
            return false;
        }
        const vec3 = new THREE.Vector3();
        vec3.set(pos.x,pos.y,1);
        const vecResult = vec3.applyMatrix3(this.matrixAbsCanvasFromAbsUV);

        return (vecResult.x >= this.margin.x && vecResult.y >= this.margin.y
        && vecResult.x <= this.textureSize.x - this.margin.x && vecResult.y <= this.textureSize.y - this.margin.y);
    }

    /**
     * Gibt die Koordinaten auf dem Canvas des Parts zurück, auf die der Punkt x/y durch Skalierung, Translation und Rotation
     * abgebildet wird
     */
    getCoordinatesAbsUVFromAbsCanvas(x, y) {
        const vec3 = new THREE.Vector3();
        vec3.set (x,y,1);
        const applied =  vec3.applyMatrix3(this.matrixAbsUVFromAbsCanvas);
        return new Vector2(applied.x, applied.y);
    }

    private static getCoordinateExtremum(minOrMaxFunction, dimension, arrayOfPoints:Vector2[]) {
        return Math.round(minOrMaxFunction(...arrayOfPoints.map(c=>c[dimension])));
    }

    private static getMaxX = curry(PartSelectableObject.getCoordinateExtremum, Math.max, "x");
    private static getMinX = curry(PartSelectableObject.getCoordinateExtremum, Math.min, "x");
    private static getMaxY = curry(PartSelectableObject.getCoordinateExtremum, Math.max, "y");
    private static getMinY = curry(PartSelectableObject.getCoordinateExtremum, Math.min, "y");

    /**
     * Gibt die Extrema der x und y Werte auf dem Canvas des Parts zurück, auf die das SelectableObject abgebildet wird.
     * errechnet sich aus den Koordinaten der 4 Ecken
     * @return {object.<number,number,number,number>}   besteht aus minX,minY,maxX,maxY
     */
    getPartCanvasMinMaxCoordinates() {
        return {
            minX: PartSelectableObject.getMinX(this.corners),
            maxX: PartSelectableObject.getMaxX(this.corners),
            minY: PartSelectableObject.getMinY(this.corners),
            maxY: PartSelectableObject.getMaxY(this.corners)
        };
    }

    getHeigtInCMFromLocalCanvas():number{
        const minY =  PartSelectableObject.getMinY(this.cornersInLocalCanvas);
        const maxY = PartSelectableObject.getMaxY(this.cornersInLocalCanvas);
        const heightInPixels = (maxY - minY) * this.scaling.y;
        return heightInPixels / this.parentDimensionInfos.textureSize.y * this.parentDimensionInfos.partSizeCM.y;
    }

    calculateSnap(point:Vector2):Vector2 {

        const utilityPainter = this.getParent().parent.utilityPainter;

        const extremes = this.getPartCanvasMinMaxCoordinates();
        const result = utilityPainter.snapsForCoordinate(
            point,
            new WidthHeight(extremes.maxX - extremes.minX, extremes.maxY - extremes.minY),
            this.id,
            this.getParent().parent.zoomController.currentSnapRange()
        );
        return result.point !== null ? result.point : point;
    }

    private cloneOnPortalLine(line:PortalLine, index:number) {
        this.clonedByPortalLineNumber.push(index);
        const cloneValues = line.calculateCloneValues(this) as {id,part,translation, rotation, scaling, cloneCloningAngleRad, origCloningAngleRad};

        const parameters = {
            id: cloneValues.id,
            newPart: cloneValues.part,
            newTranslation: this.getRelUVFromAbsUV(cloneValues.translation),
            newRotation: cloneValues.rotation,
            newRelSize: this.getRelSize(cloneValues.scaling),
            origCloningAngleRad: cloneValues.origCloningAngleRad,
            cloneCloningAngleRad: cloneValues.cloneCloningAngleRad
        };
        this.getParent().parent.selObController.cloneObject(parameters);
    }

    /**
     * checkt, ob das Objekt beim Einfügen eine Portallinie schneidet und klont das Objekt wenn zutreffend. Ruft Controller auf
     * @return {boolean}
     */
    handlePartTransitionOnInsert() {
        const $this = this;
        this.getParent().parent.getPortalLines().forEach(
            function(line, index){
                if($this.clonedByPortalLineNumber.indexOf(index) > -1 || !line.intersectsObject($this)) {
                    return;
                }
                $this.cloneOnPortalLine(line, index);
            }
        );
    }

    createHandles():Handles|null {
        if (this.getParent() === null) {
            return null;
        }
        return new Handles(this, this.getParent().handles);
    }

    zIndex = ()=> this.getParent().zOrderTable.getZIndex(this.id);

    /**
     * erstellt eine Matrix die im Shader verwendet werden kann, um auszurechnen, ob ein Pixel durch das Logo getroffen wurde.
     * Die matrix berechnet sich aus 4 Einzelmatrizen, die aneinander multipliziet werden.
     *
     * Reihenfolge der Multiplikation: rechts heißt früher.
     * Legende:
     * * T0:    Verschieben des Objekts von zentriertem Nullpunkt auf Linke untere Ecke (0/0)
     * * S:     Skalierung
     * * R:     Rotation
     * * T:     Translation des gesamten Objekts
     * Die Rückwärts Matrix ist dann also
     *<strong> WICHTIG!!!!!</strong> Zuerst! Drehen, dann skalieren (dank PartStauchungsfaktor, das Endergebnis soll in X Richtung gestaucht werden!) </strong>
     *          TO * S * R * T
     *      ergibt:
     *          cos(alpha) * Sx     |   -sin(alpha) * Sy    |   cos(alpha) * Sx * (-Tx) + (-sin(alpha)) * Sy * (Ty - GesamtY) * LogoX * 0.5
     *          sin(alpha) * Sx     |    cos(alpha) * Sy    |   sin(alpha) * Sx * (-Tx) + cos(alpha)    * Sy * (Ty - GesamtY) * LogoY * 0.5
     *                  0           |           0           |                       1
     *
     *
     */
    createMatrixAbsCanvasFromAbsUV():THREE.Matrix3 {
        const rotRad  = this.rotation * 2 * Math.PI / 360,
            scalingX=  1 / this.scaling.x / this.parentDimensionInfos.partStauchungsFaktor ,
            scalingY=  1 / this.scaling.y,
            cosRot  = Math.cos(rotRad),
            sinRot  = Math.sin(rotRad),
            matrix = new THREE.Matrix3();

        matrix.set (
            cosRot  * scalingX,
            -sinRot * scalingY,
            cosRot  * scalingX * (-this.translation.x)  - sinRot * scalingY * (-this.translation.y) + 0.5 * this.textureSize.x,
            sinRot  * scalingX,
            cosRot  * scalingY,
            sinRot  * scalingX * (-this.translation.x)  + cosRot * scalingY * (-this.translation.y) + 0.5 * this.textureSize.y,
            0,0,1
        );
        return matrix;
    }

    /**
     * Vorwärtsmatrix.
     * T * R * S * T0
     */
    createMatrixAbsUVFromAbsCanvas() {
        const rotRad  = - this.rotation * 2 * Math.PI / 360, //inverser Drehwinkel!
            scalingX=  1/(1 / this.scaling.x / this.parentDimensionInfos.partStauchungsFaktor),
            scalingY=  1/(1 / this.scaling.y),
            cosRot  = Math.cos(rotRad),
            sinRot  = Math.sin(rotRad),
            matrix = new THREE.Matrix3();


        matrix.set(
            scalingX        * cosRot,
            scalingX        * (-sinRot),
            -0.5 * scalingX * (cosRot       * this.textureSize.x - sinRot * this.textureSize.y) + this.translation.x,
            scalingY        * sinRot,
            scalingY        * cosRot,
            -0.5 * scalingY * (sinRot       * this.textureSize.x + cosRot * this.textureSize.y) + this.translation.y,
            0,0,1);
        return matrix;
    }

    /**
     * Liefert die relativen Dimensionen des Textobjects  auf dem Part sowie den tatsächlichen Mittelpunkt, wenn alles "Fleisch" weggeschnitten wird
     * @return  {object.<id, width, height, actualMiddleCoordOnPart>}
     */
    getRelUVBoundingBoxData():{id, width, height, actualMiddleCoordOnPart} {
        const extrema = this.getPartCanvasMinMaxCoordinates();
        const width   = extrema.maxX - extrema.minX;
        const height  = extrema.maxY - extrema.minY;
        const coord = new Vector2(
            (extrema.maxX + extrema.minX ) / 2 ,
            (extrema.maxY + extrema.minY ) / 2
        ).apply(Math.floor);

        return {
            id : this.id,
            width: this.getRelUVFromAbsUV_x(width),
            height : this.getRelUVFromAbsUV_y(height),
            actualMiddleCoordOnPart: this.getRelUVFromAbsUV(coord)
        };
    }

    // noinspection DuplicatedCode
    copyFromSelOb(selOb) {
    //FIXME ok, einiges muss leicht angepasst werden, aber wenn eine neue Eigenschaft hinzukommt muss man dran denken,, das auch hier einzutragen. Das geht schöner!
        this.translation = this.getAbsUVFromRelUV(selOb.translation.x, selOb.translation.y);
        this.relSize = new Vector2(selOb.relSize.x, selOb.relSize.y);
        this.setScalingFromRelSize(selOb.relSize);
        this.rotation = selOb.rotation;
        this.repeat = selOb.repeat;
        this.clampToColor = selOb.clampToColor;
        this.wasserzeichen = selOb.wasserzeichen;
        this.wasserzeichenInvertImage = selOb.wasserzeichenInvertImage;
        this.wasserzeichenIntensity = selOb.wasserzeichenIntensity;
        this.floating = selOb.floating;
        this.isPinned = selOb.isPinned;
        this.hasTackleTwill = selOb.hasTackleTwill;
        this.allowDrag = selOb.allowDrag;
        this.allowResize = selOb.allowResize;
        this.allowDelete = selOb.allowDelete;
        this.isVorbelegung = selOb.isVorbelegung;
        if (selOb.cloneIds.length === 0) {
            this.clonedByPortalLineNumber = [];
        }
    }
}

